import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Switch,
  Text,
  useBoolean,
  useOutsideClick,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import DeleteModal from 'components/Modals/DeleteModal';
import DuplicateIconV1 from 'components/Icons/DuplicateIconV1';
import FigmaCheckCircleIcon from 'components/Icons/FigmaCheckCircleIcon';
import FigmaEditIcon from 'components/Icons/FigmaEditIcon';
import FigmaMenuDotsIcon from 'components/Icons/FigmaMenuDotsIcon';
import FigmaTrashIcon from 'components/Icons/FigmaTrashIcon';
// Same shared chevron DimensionsRevamp's scenario trigger uses — a thin
// stroked caret. The plan-local icon was a filled triangle, which made this
// control read heavier than the identical one on the Dimensions header.
import FigmaTriangleDown from 'components/Icons/FigmaTriangleDown';
// Styled 1:1 off DashboardRevamp's Scenario Comparison dialog (icons, hex
// values, Popover-based rows and all) rather than a second hand-tuned
// version, so the two never drift apart again. Not a literal import of that
// component: Plan's compare flow is draft-then-Apply while Dashboard's
// applies each click immediately, and the two read from different slices.
import FigmaCodeCompareIcon from 'components/Icons/FigmaCodeCompareIcon';
import { Scenario } from 'models/response/scenario.interface';
import { DeleteScenarios, DuplicateScenarios, RenameScenario } from 'redux/BuilderModeSlice';
import { groupByFilterChange } from 'redux/PlanPageSlice';
import {
  fetchAllScenarios,
  getAllScenarios,
  getBaseScenario,
  getCompareScenario,
  getToggleScenario,
  selectBaseScenario,
  selectCompareScenario,
  toggleCompareScenario,
} from 'redux/ScenarioSlice';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

import {
  PLAN_ACCENT_BLUE,
  PLAN_COMPARE_SCENARIO_DOT,
  PLAN_TEXT,
  controlHeight,
  neutral,
  text as textToken,
} from '../planPageRevampUi';

/** Edit / Duplicate / Delete for one row — only the Base Scenario field gets
 * this, matching Dashboard (its Compare Against list is select-only too). */
interface ScenarioRowActions {
  renamingId: number | null;
  onStartRename: (scenario: Scenario) => void;
  onDuplicate: (scenario: Scenario) => void;
  onRequestDelete: (scenario: Scenario) => void;
  canDelete: boolean;
}

interface ScenarioFieldProperties {
  label: string;
  dotColor: string;
  selectedScenario: Scenario | null;
  scenarios: Scenario[];
  excludedScenarioId?: number;
  onSelect: (scenario: Scenario) => void;
  rowActions?: ScenarioRowActions;
  renameValue?: string;
  onRenameValueChange?: (value: string) => void;
  onRenameSubmit?: () => void;
  onRenameCancel?: () => void;
}

const PlanScenarioSelectField = memo(
  ({
    label,
    dotColor,
    selectedScenario,
    scenarios,
    excludedScenarioId,
    onSelect,
    rowActions,
    renameValue,
    onRenameValueChange,
    onRenameSubmit,
    onRenameCancel,
  }: ScenarioFieldProperties) => {
    const [isOpen, setOpen] = useBoolean();
    const [openRowMenuId, setOpenRowMenuId] = useState<number | null>(null);
    const reference = useRef<HTMLDivElement>(null);
    // Fixed at 60vh the list could still run past the bottom of a short
    // browser window when the field itself sits low on the page — size it off
    // the trigger's actual position instead so it always fits above the fold.
    const [listMaxHeight, setListMaxHeight] = useState('60vh');

    const options = useMemo(
      () => scenarios.filter((scenario) => scenario.id !== excludedScenarioId),
      [excludedScenarioId, scenarios],
    );

    useOutsideClick({
      ref: reference,
      handler: () => {
        setOpen.off();
        setOpenRowMenuId(null);
      },
    });

    const openDropdown = () => {
      const bottom = reference.current?.getBoundingClientRect().bottom ?? 0;
      const available = window.innerHeight - bottom - 16;
      setListMaxHeight(`${Math.max(160, Math.min(available, window.innerHeight * 0.6))}px`);
      setOpen.toggle();
    };

    return (
      <Box>
        <Text color='#708090' fontFamily='Roboto Regular' fontWeight='600' fontSize='11px' mb='4px'>
          {label}
        </Text>
        {/* A plain Popover, not Chakra's Menu — a per-row 3-dot menu needs to
            open without the parent auto-closing on click, which Menu/MenuItem
            fight you on. Same approach Dashboard's own dropdown uses. */}
        <Popover placement='bottom-start' closeOnBlur={false} isOpen={isOpen} isLazy>
          <Box ref={reference}>
            <PopoverTrigger>
              <Flex
                alignItems='center'
                justifyContent='space-between'
                bg='white'
                border='1px solid #D9D9D9'
                borderRadius='9px'
                p='11px'
                cursor='pointer'
                _hover={{ borderColor: '#8B5CF6' }}
                onClick={openDropdown}
              >
                <Flex alignItems='center' gap='11px' flex={1} minW={0}>
                  <Box w='7px' h='7px' borderRadius='50%' bg={dotColor} flexShrink={0} />
                  <Text
                    fontFamily='Roboto Regular'
                    fontSize='13px'
                    color='#121826'
                    noOfLines={1}
                    textAlign='left'
                  >
                    {selectedScenario?.name ?? 'Select Scenario'}
                  </Text>
                </Flex>
                <FigmaTriangleDown width={9} height={5} color='#8B8AAB' />
              </Flex>
            </PopoverTrigger>
            <PopoverContent
              boxShadow='0px 4px 24px rgba(0, 0, 0, 0.25)'
              bg='#E8E9EB'
              borderRadius='14px'
              border='none'
              overflow='hidden'
              minW='302px'
              maxH={listMaxHeight}
              outline='none'
              _focus={{ boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.25)' }}
              _focusVisible={{ boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.25)' }}
            >
              <PopoverBody p={0} maxH={listMaxHeight} overflowY='auto'>
                <Flex direction='column' gap='1px'>
                  {options.map((scenario, index) => {
                    const isSelected = selectedScenario?.id === scenario.id;
                    const isRenaming = rowActions?.renamingId === scenario.id;

                    if (isRenaming) {
                      return (
                        <Flex
                          key={scenario.id}
                          alignItems='center'
                          gap='9px'
                          bg='white'
                          px='14px'
                          py='9px'
                        >
                          <Input
                            autoFocus
                            size='sm'
                            value={renameValue}
                            onChange={(event) => onRenameValueChange?.(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') onRenameSubmit?.();
                              if (event.key === 'Escape') onRenameCancel?.();
                            }}
                            fontFamily='Roboto Regular'
                            fontSize='13px'
                            borderRadius='7px'
                            border='1px solid #8B5CF6'
                            _focusVisible={{ borderColor: '#8B5CF6', boxShadow: 'none' }}
                          />
                          <Text
                            as='button'
                            type='button'
                            color='#8B5CF6'
                            fontFamily='Roboto Medium, Roboto Regular'
                            fontWeight='500'
                            fontSize='13px'
                            onClick={onRenameSubmit}
                          >
                            Save
                          </Text>
                        </Flex>
                      );
                    }

                    return (
                      <Flex
                        key={scenario.id}
                        alignItems='center'
                        gap='9px'
                        cursor='pointer'
                        bg='white'
                        px='14px'
                        py='11px'
                        onClick={() => {
                          onSelect(scenario);
                          setOpen.off();
                          setOpenRowMenuId(null);
                        }}
                      >
                        <Text
                          flex={1}
                          fontFamily='Roboto Regular'
                          fontSize='13px'
                          color='#121826'
                          whiteSpace='nowrap'
                          overflow='hidden'
                          textOverflow='ellipsis'
                        >
                          {scenario.name}
                        </Text>
                        {isSelected ? (
                          <FigmaCheckCircleIcon width={14} height={14} color='#8B5CF6' />
                        ) : (
                          <Box
                            width='14px'
                            height='14px'
                            borderRadius='50%'
                            bg='white'
                            border='1px solid #C4B5FD'
                          />
                        )}
                        {rowActions && (
                          <Flex alignItems='center' position='relative'>
                            <Flex
                              alignItems='center'
                              cursor='pointer'
                              onClick={(event) => {
                                event.stopPropagation();
                                setOpenRowMenuId((current) =>
                                  current === scenario.id ? null : scenario.id,
                                );
                              }}
                            >
                              <FigmaMenuDotsIcon width={14} height={14} color='#121826' />
                            </Flex>
                            <Flex
                              bg='white'
                              width='153px'
                              flexDirection='column'
                              position='absolute'
                              display={openRowMenuId === scenario.id ? 'flex' : 'none'}
                              zIndex='dropdown'
                              top={
                                options.length > 3 && index >= options.length - 3
                                  ? '-100px'
                                  : '28px'
                              }
                              right='0'
                              overflow='hidden'
                              borderRadius='8px'
                              boxShadow='0px 4px 24px rgba(0, 0, 0, 0.25)'
                            >
                              <Flex
                                as='button'
                                alignItems='center'
                                gap='8px'
                                px='11px'
                                py='9px'
                                fontFamily='Roboto Regular'
                                fontSize='13px'
                                color='#121826'
                                _hover={{ bg: '#F5F5F5' }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setOpenRowMenuId(null);
                                  rowActions.onStartRename(scenario);
                                }}
                              >
                                <FigmaEditIcon width={14} height={14} color='#121826' />
                                Edit
                              </Flex>
                              <Flex
                                as='button'
                                alignItems='center'
                                gap='8px'
                                px='11px'
                                py='9px'
                                fontFamily='Roboto Regular'
                                fontSize='13px'
                                color='#121826'
                                _hover={{ bg: '#F5F5F5' }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setOpenRowMenuId(null);
                                  rowActions.onDuplicate(scenario);
                                }}
                              >
                                <Flex
                                  w='14px'
                                  h='14px'
                                  align='center'
                                  justify='center'
                                  flexShrink={0}
                                >
                                  <DuplicateIconV1 />
                                </Flex>
                                Duplicate
                              </Flex>
                              {rowActions.canDelete && (
                                <Flex
                                  as='button'
                                  alignItems='center'
                                  gap='8px'
                                  px='11px'
                                  py='9px'
                                  fontFamily='Roboto Regular'
                                  fontSize='13px'
                                  color='#E23F3F'
                                  _hover={{ bg: '#FEF0F0' }}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setOpenRowMenuId(null);
                                    rowActions.onRequestDelete(scenario);
                                  }}
                                >
                                  <FigmaTrashIcon width={14} height={14} color='#E23F3F' />
                                  Delete
                                </Flex>
                              )}
                            </Flex>
                          </Flex>
                        )}
                      </Flex>
                    );
                  })}
                </Flex>
              </PopoverBody>
            </PopoverContent>
          </Box>
        </Popover>
      </Box>
    );
  },
);

PlanScenarioSelectField.displayName = 'PlanScenarioSelectField';

interface Properties {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  refetchRequest: () => void;
  persistScenario: (scenario: Scenario) => void;
}

const PlanScenarioComparisonPopover = ({
  isOpen,
  onOpen,
  onClose,
  refetchRequest,
  persistScenario,
}: Properties) => {
  const dispatch: AppDispatch = useDispatch();
  const { modelId } = useParams();
  const themeColors = useSelector(getPlanPageThemeColors);
  const scenarios = useSelector(getAllScenarios);
  const baseScenario = useSelector(getBaseScenario);
  const compareScenario = useSelector(getCompareScenario);
  const compareEnabled = useSelector(getToggleScenario);

  const [draftBaseScenario, setDraftBaseScenario] = useState<Scenario | null>(null);
  const [draftCompareEnabled, setDraftCompareEnabled] = useState(false);
  const [draftCompareScenario, setDraftCompareScenario] = useState<Scenario | null>(null);

  // Base Scenario row management — Edit / Duplicate / Delete, matching
  // Dashboard's Scenario Comparison dialog.
  const [renameTarget, setRenameTarget] = useState<Scenario | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Scenario | null>(null);

  const canCompare = (scenarios?.length ?? 0) > 1;

  const syncDraftFromStore = useCallback(() => {
    setDraftBaseScenario(baseScenario ?? null);
    setDraftCompareEnabled(Boolean(compareEnabled));
    setDraftCompareScenario(compareScenario ?? null);
  }, [baseScenario, compareEnabled, compareScenario]);

  useEffect(() => {
    if (isOpen) {
      syncDraftFromStore();
    } else {
      setRenameTarget(null);
      setDeleteTarget(null);
    }
  }, [isOpen, syncDraftFromStore]);

  const pickDefaultCompareScenario = useCallback(
    (base: Scenario | null) => {
      if (!base) return null;
      return scenarios.find((scenario: Scenario) => scenario.id !== base.id) ?? null;
    },
    [scenarios],
  );

  const onDraftCompareToggle = useCallback(
    (enabled: boolean) => {
      setDraftCompareEnabled(enabled);
      if (enabled) {
        setDraftCompareScenario(
          (current) => current ?? pickDefaultCompareScenario(draftBaseScenario),
        );
      } else {
        setDraftCompareScenario(null);
      }
    },
    [draftBaseScenario, pickDefaultCompareScenario],
  );

  const onDraftBaseChange = useCallback(
    (scenario: Scenario) => {
      setDraftBaseScenario(scenario);
      if (draftCompareScenario?.id === scenario.id) {
        setDraftCompareScenario(pickDefaultCompareScenario(scenario));
      }
    },
    [draftCompareScenario?.id, pickDefaultCompareScenario],
  );

  const onCancel = useCallback(() => {
    syncDraftFromStore();
    onClose();
  }, [onClose, syncDraftFromStore]);

  const onApply = useCallback(() => {
    if (!draftBaseScenario) return;

    persistScenario(draftBaseScenario);
    dispatch(selectBaseScenario(draftBaseScenario));

    if (draftCompareEnabled && canCompare && draftCompareScenario) {
      dispatch(toggleCompareScenario(true));
      dispatch(groupByFilterChange('Total'));
      dispatch(selectCompareScenario(draftCompareScenario));
    } else {
      dispatch(toggleCompareScenario(false));
    }

    refetchRequest();
    onClose();
  }, [
    canCompare,
    dispatch,
    draftBaseScenario,
    draftCompareEnabled,
    draftCompareScenario,
    onClose,
    persistScenario,
    refetchRequest,
  ]);

  const refetchScenarios = useCallback(async (): Promise<Scenario[]> => {
    if (!modelId) return scenarios;
    const result = await dispatch(fetchAllScenarios(modelId));
    return (result?.payload as Scenario[] | undefined) ?? scenarios;
  }, [dispatch, modelId, scenarios]);

  const onStartRename = useCallback((scenario: Scenario) => {
    setRenameTarget(scenario);
    setRenameValue(scenario.name);
  }, []);

  const onRenameCancel = useCallback(() => {
    setRenameTarget(null);
    setRenameValue('');
  }, []);

  const onRenameSubmit = useCallback(async () => {
    const trimmed = renameValue.trim();
    if (!renameTarget || !trimmed || trimmed === renameTarget.name) {
      onRenameCancel();
      return;
    }
    await dispatch(
      RenameScenario({
        item_id: renameTarget.id,
        requestBody: { name: trimmed },
        toaster: {
          successMessage: 'Successfully updated',
          errorMessage: 'A scenario with this name already exists',
        },
      }),
    );
    const refreshed = await refetchScenarios();
    // The rename may have touched the currently-drafted base/compare scenario —
    // keep the draft in sync with its new name instead of showing the stale one.
    setDraftBaseScenario((current) =>
      current?.id === renameTarget.id
        ? refreshed.find((s) => s.id === renameTarget.id) ?? current
        : current,
    );
    setDraftCompareScenario((current) =>
      current?.id === renameTarget.id
        ? refreshed.find((s) => s.id === renameTarget.id) ?? current
        : current,
    );
    onRenameCancel();
  }, [dispatch, onRenameCancel, refetchScenarios, renameTarget, renameValue]);

  const onDuplicate = useCallback(
    async (scenario: Scenario) => {
      await dispatch(
        DuplicateScenarios({
          scenarioId: scenario.id,
          data: { name: `${scenario.name} - Copy` },
          toaster: {
            successMessage: 'Successfully duplicated scenario',
            errorMessage: 'Failed to duplicate',
          },
        }),
      );
      await refetchScenarios();
    },
    [dispatch, refetchScenarios],
  );

  const onConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    const deletedId = deleteTarget.id;
    await dispatch(DeleteScenarios(deletedId));
    const refreshed = await refetchScenarios();
    setDeleteTarget(null);

    // Same safety net as Dashboard's version: fall back to another scenario
    // rather than leaving the draft pointed at one that no longer exists.
    if (draftBaseScenario?.id === deletedId) {
      const fallback = refreshed.find((s: Scenario) => s.is_base) ?? refreshed[0] ?? null;
      setDraftBaseScenario(fallback);
    }
    if (draftCompareScenario?.id === deletedId) {
      setDraftCompareScenario(null);
    }
  }, [deleteTarget, dispatch, draftBaseScenario?.id, draftCompareScenario?.id, refetchScenarios]);

  const triggerLabel = useMemo(() => {
    if (compareEnabled && compareScenario?.name) {
      return `${baseScenario?.name ?? 'Scenario'} vs ${compareScenario.name}`;
    }
    return baseScenario?.name ?? 'Scenario';
  }, [baseScenario?.name, compareEnabled, compareScenario?.name]);

  if (!canCompare || !baseScenario) {
    return null;
  }

  return (
    <>
      {/* Sized off `controlHeight` so this half matches the date half and the
          32px group that wraps both — the hardcoded 40px was taller than the
          group and pushed it out of shape. Hover is a neutral surface change,
          not the accent tint, which on a resting control read as "selected".
          Same treatment as DimensionsRevamp's scenario trigger. */}
      <Flex
        as='button'
        type='button'
        alignItems='center'
        h={controlHeight}
        minH={controlHeight}
        maxW='240px'
        flexWrap='nowrap'
        bg='transparent'
        gap={'8px'}
        padding={'0 12px'}
        cursor='pointer'
        transition='background 0.12s ease'
        _hover={{ bg: neutral.surfaceHover }}
        _focus={{ outline: 'none', boxShadow: 'none' }}
        onClick={onOpen}
      >
        <Flex alignItems='center' gap={'8px'} minW={0} flex='1' flexWrap='nowrap'>
          <Box w='8px' h='8px' borderRadius='full' bg={PLAN_ACCENT_BLUE} flexShrink={0} />
          {compareEnabled && compareScenario && (
            <Box
              w='8px'
              h='8px'
              borderRadius='full'
              bg={PLAN_COMPARE_SCENARIO_DOT}
              flexShrink={0}
            />
          )}
          <Text
            {...PLAN_TEXT.toolbarTab}
            color={textToken.primary}
            noOfLines={1}
            minW={'120px'}
            flex='1'
            textAlign='left'
            whiteSpace='nowrap'
          >
            {triggerLabel}
          </Text>
        </Flex>
        <Box flexShrink={0} display='flex' alignItems='center'>
          <FigmaTriangleDown width={10} height={6} color={themeColors.primary} />
        </Box>
      </Flex>

      {/* Centered modal with backdrop, styled 1:1 off DashboardRevamp/SelectScenario's
          Scenario Comparison dialog (down to the hardcoded colours) rather than a
          corner-anchored dropdown or a second hand-tuned palette — see the import
          comment above for why this isn't the Dashboard component itself. */}
      <Modal isOpen={isOpen} onClose={onCancel} isCentered>
        <ModalOverlay />
        <ModalContent
          boxShadow='0px 4px 12px rgba(0, 0, 0, 0.25)'
          bg='white'
          width='338px'
          maxWidth='90vw'
          borderRadius='14px'
          p='18px'
        >
          <Box textAlign='center'>
            <Text color='#121826' fontFamily='Comfortaa' fontWeight='bold' fontSize='18px'>
              Scenario Comparison
            </Text>
            <Text color='#708090' fontFamily='Roboto Regular' fontSize='13px' mt={2}>
              Choose which scenarios to compare in variance cards and KPIs
            </Text>
          </Box>

          <ModalBody px={0} pb={0} pt='18px'>
            <Flex direction='column' gap='18px'>
              <PlanScenarioSelectField
                label='Base Scenario'
                dotColor={PLAN_ACCENT_BLUE}
                selectedScenario={draftBaseScenario}
                scenarios={scenarios}
                excludedScenarioId={draftCompareEnabled ? draftCompareScenario?.id : undefined}
                onSelect={onDraftBaseChange}
                rowActions={{
                  renamingId: renameTarget?.id ?? null,
                  onStartRename,
                  onDuplicate,
                  onRequestDelete: setDeleteTarget,
                  canDelete: (scenarios?.length ?? 0) > 1,
                }}
                renameValue={renameValue}
                onRenameValueChange={setRenameValue}
                onRenameSubmit={onRenameSubmit}
                onRenameCancel={onRenameCancel}
              />

              <Flex align='center' gap='11px' bg='#F5F0FF' borderRadius='11px' px='11px' py='9px'>
                <FigmaCodeCompareIcon width={14} height={14} color='#121826' />
                <Text flex={1} fontFamily='Roboto Regular' fontSize='13px' color='#121826'>
                  Compare
                </Text>
                <Switch
                  size='sm'
                  colorScheme='purple'
                  isChecked={draftCompareEnabled}
                  onChange={(event) => onDraftCompareToggle(event.target.checked)}
                />
              </Flex>

              {draftCompareEnabled && (
                <PlanScenarioSelectField
                  label='Compare Against'
                  dotColor={PLAN_COMPARE_SCENARIO_DOT}
                  selectedScenario={draftCompareScenario}
                  scenarios={scenarios}
                  excludedScenarioId={draftBaseScenario?.id}
                  onSelect={setDraftCompareScenario}
                />
              )}
            </Flex>

            <Flex gap='18px' mt='18px'>
              <Button
                flex={1}
                bg='white'
                border='1px solid #D9D9D9'
                borderRadius='11px'
                fontFamily='Roboto Regular'
                fontWeight='500'
                fontSize='13px'
                color='#121826'
                p='14px'
                height='auto'
                _hover={{ bg: 'gray.50' }}
                _focus={{ boxShadow: 'none' }}
                _focusVisible={{ boxShadow: 'none' }}
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                flex={1}
                bg='#8B5CF6'
                borderRadius='11px'
                fontFamily='Roboto Bold'
                fontWeight='700'
                fontSize='13px'
                color='white'
                p='14px'
                height='auto'
                _hover={{ bg: '#7C4FE0' }}
                _focus={{ boxShadow: 'none' }}
                _focusVisible={{ boxShadow: 'none' }}
                onClick={onApply}
                isDisabled={!draftBaseScenario || (draftCompareEnabled && !draftCompareScenario)}
              >
                Apply
              </Button>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>

      <DeleteModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        itemName={deleteTarget?.name ?? ''}
        onDelete={onConfirmDelete}
      />
    </>
  );
};

export default memo(PlanScenarioComparisonPopover);
